/**
 * Asteroids (IPM, unlocked by the Asteroid Miner project): every minute or
 * two an asteroid drifts through the galaxy. Tap it before it leaves for a
 * burst of one ore. IPM describes it as "a fat pulse of a random ore"; the
 * size here — 45 seconds of the whole fleet's mining — is provisional.
 */

import { PLANETS } from '../content/planets';
import { mineRate } from './economy';
import { hasFeature } from './projects';
import { nextRandom, randomRange } from './rng';
import type { FleetStats } from './stats';
import type { Emit } from './events';
import type { GameState } from './state';
import { addToHold } from './tick';

export const ASTEROID_EVERY_MIN = 60;
export const ASTEROID_EVERY_MAX = 120;
export const ASTEROID_LIFETIME = 25;
export const ASTEROID_SECONDS_OF_MINING = 45;

export function stepAsteroids(state: GameState, stats: FleetStats, dt: number): void {
  const run = state.run;
  if (!hasFeature(state, 'asteroids')) return;
  if (run.asteroid) {
    run.asteroid.x += run.asteroid.vx * dt;
    run.asteroid.y += run.asteroid.vy * dt;
    if (run.time >= run.asteroid.until) {
      run.asteroid = null;
      run.nextAsteroidAt = run.time + randomRange(state, ASTEROID_EVERY_MIN, ASTEROID_EVERY_MAX);
    }
    return;
  }
  if (run.nextAsteroidAt === 0) run.nextAsteroidAt = run.time + ASTEROID_EVERY_MIN / 2;
  if (run.time < run.nextAsteroidAt) return;

  // A random ore from a random owned planet, sized to the fleet's total mining.
  const owned = run.planets.map((p, i) => (p.owned ? i : -1)).filter((i) => i >= 0);
  if (owned.length === 0) return;
  const planet = owned[Math.floor(nextRandom(state) * owned.length)]!;
  const ores = PLANETS[planet]!.ores;
  const ore = ores[Math.floor(nextRandom(state) * ores.length)]!.id;
  const fleetMining = owned.reduce((sum, i) => sum + mineRate(state, stats, i), 0);
  const angle = nextRandom(state) * Math.PI * 2;
  const r = randomRange(state, 50, 130);
  run.asteroid = {
    ore,
    amount: fleetMining * ASTEROID_SECONDS_OF_MINING,
    until: run.time + ASTEROID_LIFETIME,
    x: Math.cos(angle) * r,
    y: Math.sin(angle) * r * 0.62,
    vx: randomRange(state, -3, 3),
    vy: randomRange(state, -2, 2),
  };
}

export function collectAsteroid(state: GameState, emit: Emit): { ore: string; amount: number } | null {
  const a = state.run.asteroid;
  if (!a) return null;
  addToHold(state, a.ore, a.amount, emit);
  state.run.asteroid = null;
  state.run.nextAsteroidAt = state.run.time + randomRange(state, ASTEROID_EVERY_MIN, ASTEROID_EVERY_MAX);
  return { ore: a.ore, amount: a.amount };
}
